import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertFromHTML } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import myAxios from '../AxiosUrl';
import { ImageUpload, uploadCallback } from '../AddProduct/HelperFunction';
import styles from './SEOManagement.module.css';
import { AiOutlineSave, AiOutlineEye, AiOutlinePlus, AiOutlineDelete } from 'react-icons/ai';

// Storefront SEO section texts (Section Texts tab): key, label, default.
const TEXT_FIELDS = [
    ['faqTitle', 'FAQ heading (home and category pages)', 'Frequently Asked Questions', 80],
    ['categoriesLabel', 'Label before the category links (home page)', 'Shop by category', 60],
    ['readMore', '"Read more" button', 'Read more', 30],
    ['showLess', '"Show less" button', 'Show less', 30],
    ['homeLabel', 'First breadcrumb item (category pages)', 'Home', 30],
    ['productsLabel', 'Word after the product count (category pages)', 'products', 30],
    ['descriptionTab', 'Description tab (product pages)', 'Description', 30],
    ['faqTab', 'FAQ tab (product pages; the number of questions is added)', 'FAQs', 30],
    ['reviewsTitle', 'Reviews heading (product pages)', 'Customer Reviews', 60],
    ['reviewsLabel', 'Word after the review count', 'reviews', 30],
    ['noReviews', 'Text when a product has no reviews yet', 'No reviews yet. Be the first to review this product.', 160],
    ['verifiedLabel', 'Badge on a verified review', 'Verified purchase', 40],
    ['writeReview', '"Write a review" button', 'Write a review', 40],
    ['reviewName', 'Review form: name label', 'Your name', 40],
    ['reviewRating', 'Review form: rating label', 'Your rating', 40],
    ['reviewText', 'Review form: review label', 'Your review', 40],
    ['reviewSubmit', 'Review form: submit button', 'Submit review', 40],
    ['reviewThanks', 'Message after a review is sent', 'Thank you! Your review will appear after the store approves it.', 160],
    ['reviewError', 'Message if a review could not be sent', 'Sorry, your review could not be sent. Please try again.', 160],
];
// Site header and footer labels; the top bar text and the footer's about text
// (below) are made from the store settings when empty
const CHROME_FIELDS = [
    ['moreLabel', 'Menu: button for the categories after the first 4', 'More', 30],
    ['callLabel', 'Menu: call button', 'Call', 30],
    ['whatsappLabel', 'Header: WhatsApp link', 'WhatsApp', 30],
    ['cartLabel', 'Header: cart button name (read by screen readers)', 'Cart', 30],
    ['footerCategories', 'Footer: categories heading', 'Categories', 40],
    ['footerInfo', 'Footer: policy pages heading', 'Information', 40],
    ['footerContact', 'Footer: contact heading', 'Contact Us', 40],
    ['footerFollow', 'Footer: social icons heading', 'Follow Us', 40],
    ['rightsText', 'Footer: text after "© year store name."', 'All rights reserved.', 80],
];
const TEXT_DEFAULTS_EMPTY = Object.fromEntries([...TEXT_FIELDS, ...CHROME_FIELDS, ['topbarText'], ['footerAbout']].map(([k]) => [k, '']));
const FACT_ICONS = [['cash', 'Cash'], ['truck', 'Delivery'], ['phone', 'Phone'], ['chat', 'WhatsApp / chat'], ['star', 'Star'], ['shield', 'Shield']];

function HomePageSEO() {
    const uid = localStorage.getItem('id');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('meta');
    const [aiLoading, setAiLoading] = useState(false);
    const [showAiPrompt, setShowAiPrompt] = useState(false);
    const [aiAbout, setAiAbout] = useState('');
    const [aiStoreType, setAiStoreType] = useState('');
    const [aiSection, setAiSection] = useState('all');

    // Meta Tags state
    const [metaTitle, setMetaTitle] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [metaKeywords, setMetaKeywords] = useState('');
    const [ogImage, setOgImage] = useState('');
    const [ogImageWidth, setOgImageWidth] = useState('800');
    const [ogImageHeight, setOgImageHeight] = useState('800');
    const [ogDescription, setOgDescription] = useState('');
    const [facebookAppId, setFacebookAppId] = useState('');

    // AI & Structured Data state
    const [storeDescription, setStoreDescription] = useState('');
    const [storeTagline, setStoreTagline] = useState('');
    const [seoLanguage, setSeoLanguage] = useState('bn');
    const [robotsMeta, setRobotsMeta] = useState('index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    const [seoAuthor, setSeoAuthor] = useState('');
    const [structuredDataType, setStructuredDataType] = useState('OnlineStore');
    const [storeUrl, setStoreUrl] = useState('');

    // Home Page Article state
    const [articleEnabled, setArticleEnabled] = useState(false);
    const [articleTitle, setArticleTitle] = useState('');
    const [articlePosition, setArticlePosition] = useState('above_footer');
    const [editorState, setEditorState] = useState(EditorState.createEmpty());

    // Homepage FAQs state
    const [homePageFaqs, setHomePageFaqs] = useState([]);

    // Section texts: the words of the storefront SEO sections (empty = default)
    const [seoTexts, setSeoTexts] = useState({ ...TEXT_DEFAULTS_EMPTY, showFacts: true, showTopbar: true, facts: [] });
    const [storeFacts, setStoreFacts] = useState({ Dhaka: '', Outside: '', number: '', WhatsApp: '' });

    // OG Image upload
    const [uploadingImage, setUploadingImage] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    // Facebook needs the share image's exact size: read it from the image
    // itself whenever the image changes (upload, pasted URL or saved value).
    useEffect(() => {
        if (!ogImage) return undefined;
        let live = true;
        const img = new Image();
        img.onload = () => {
            if (!live || !img.naturalWidth) return;
            setOgImageWidth(String(img.naturalWidth));
            setOgImageHeight(String(img.naturalHeight));
        };
        img.src = ogImage;
        return () => { live = false; };
    }, [ogImage]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await myAxios.get(`/get-home-seo/${uid}`);
            if (response.data.success) {
                const d = response.data.data;
                setStoreUrl(d.storeUrl || '');
                setMetaTitle(d.metaTitle || '');
                setMetaDescription(d.metaDescription || '');
                setMetaKeywords(d.metaKeywords || '');
                setOgImage(d.ogImage || '');
                setOgImageWidth(d.ogImageWidth || '800');
                setOgImageHeight(d.ogImageHeight || '800');
                setOgDescription(d.ogDescription || '');
                setFacebookAppId(d.facebookAppId || '');
                setStoreDescription(d.storeDescription || '');
                setStoreTagline(d.storeTagline || '');
                setSeoLanguage(d.seoLanguage || 'bn');
                setRobotsMeta(d.robotsMeta || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
                setSeoAuthor(d.seoAuthor || '');
                setStructuredDataType(d.structuredDataType || 'OnlineStore');

                // Article
                const article = d.homePageArticle || {};
                setArticleEnabled(article.enabled || false);
                setArticleTitle(article.title || '');
                setArticlePosition(article.position || 'above_footer');

                // Set editor content from HTML
                if (article.content) {
                    const blocksFromHTML = convertFromHTML(article.content);
                    const contentState = ContentState.createFromBlockArray(
                        blocksFromHTML.contentBlocks,
                        blocksFromHTML.entityMap
                    );
                    setEditorState(EditorState.createWithContent(contentState));
                }

                // FAQs
                setHomePageFaqs(d.homePageFaqs || []);

                // Section texts + the settings the automatic facts come from
                const t = d.seoTexts || {};
                setSeoTexts({
                    ...Object.fromEntries(Object.keys(TEXT_DEFAULTS_EMPTY).map((k) => [k, t[k] || ''])),
                    showFacts: t.showFacts !== false,
                    showTopbar: t.showTopbar !== false,
                    facts: (t.facts || []).map((f) => ({ icon: f.icon || 'star', title: f.title || '', text: f.text || '' })),
                });
                setStoreFacts({ Dhaka: '', Outside: '', number: '', WhatsApp: '', ...(d.storeFacts || {}) });
            }
        } catch (error) {
            console.error('Error fetching home page SEO:', error);
            if (error.response?.status !== 404) {
                toast.error('Failed to load SEO settings');
            }
        } finally {
            setLoading(false);
        }
    };

    const startAIGenerate = () => {
        if (!aiStoreType) setAiStoreType(metaKeywords || storeTagline || '');
        if (!aiAbout) setAiAbout(storeDescription || metaDescription || '');
        setShowAiPrompt(true);
    };

    const handleAIGenerate = async () => {
        const storeName = localStorage.getItem('storeName') || 'My Store';
        setShowAiPrompt(false);
        setAiLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await fetch('https://api.ecomtechbd.com/home-seo/ai-generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', token, uid },
                body: JSON.stringify({ storeName, storeType: aiStoreType.trim(), aboutStore: aiAbout.trim(), section: aiSection }),
            });
            if (!res.ok) throw new Error('Server error');
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const d = JSON.parse(line.slice(6));
                        if (d.type === 'complete' && d.success) {
                            const r = d.data;
                            const s = aiSection;
                            if ((s === 'all' || s === 'meta') && r.metaTitle) setMetaTitle(r.metaTitle);
                            if ((s === 'all' || s === 'meta') && r.metaDescription) setMetaDescription(r.metaDescription);
                            if ((s === 'all' || s === 'meta') && r.metaKeywords) setMetaKeywords(r.metaKeywords);
                            if ((s === 'all' || s === 'meta') && r.ogDescription) setOgDescription(r.ogDescription);
                            if ((s === 'all' || s === 'meta' || s === 'ai') && r.storeTagline) setStoreTagline(r.storeTagline);
                            if ((s === 'all' || s === 'ai') && r.storeDescription) setStoreDescription(r.storeDescription);
                            if ((s === 'all' || s === 'article') && r.articleTitle) { setArticleTitle(r.articleTitle); setArticleEnabled(true); }
                            if ((s === 'all' || s === 'article') && r.articleContent) {
                                setArticleEnabled(true);
                                const blocksFromHTML = convertFromHTML(r.articleContent);
                                if (blocksFromHTML.contentBlocks) {
                                    const cs = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
                                    setEditorState(EditorState.createWithContent(cs));
                                }
                            }
                            if ((s === 'all' || s === 'faqs') && r.homePageFaqs?.length) {
                                setHomePageFaqs(r.homePageFaqs.map((f, i) => ({ ...f, gid: String(Date.now() + i) })));
                            }
                            toast.success('AI content generated!');
                        } else if (d.type === 'complete' && !d.success) {
                            toast.error(d.message || 'Failed');
                        }
                    } catch {}
                }
            }
        } catch (err) {
            toast.error('Failed: ' + err.message);
        } finally {
            setAiLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const articleContent = stateToHTML(editorState.getCurrentContent());
            const payload = {
                metaTitle,
                metaDescription,
                metaKeywords,
                ogImage,
                ogImageWidth,
                ogImageHeight,
                ogDescription,
                facebookAppId,
                storeDescription,
                storeTagline,
                seoLanguage,
                robotsMeta,
                seoAuthor,
                structuredDataType,
                homePageArticle: {
                    enabled: articleEnabled,
                    title: articleTitle,
                    content: articleContent,
                    position: articlePosition
                },
                homePageFaqs: homePageFaqs.filter(faq => faq.question.trim() && faq.answer.trim()),
                seoTexts: {
                    ...seoTexts,
                    facts: seoTexts.facts.filter((f) => f.title.trim() || f.text.trim()),
                },
            };

            const response = await myAxios.put(`/update-home-seo/${uid}`, payload);
            if (response.data.success) {
                toast.success('Home Page SEO saved successfully! Cache has been cleared.');
            }
        } catch (error) {
            console.error('Error saving home page SEO:', error);
            toast.error(error.response?.data?.error || 'Failed to save SEO settings');
        } finally {
            setSaving(false);
        }
    };

    const handleOgImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingImage(true);
        try {
            const imageUrl = await ImageUpload(file);
            setOgImage(imageUrl);
            toast.success('OG Image uploaded successfully');
        } catch (error) {
            toast.error('Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    // The facts the storefront makes from the store settings when the seller
    // has none of their own (SiteEjs seo.homeFacts).
    const autoFacts = () => {
        const f = [{ icon: 'cash', title: 'Cash on Delivery', text: 'Pay when you receive your order' }];
        if (storeFacts.Dhaka || storeFacts.Outside) f.push({ icon: 'truck', title: 'Delivery Charge', text: [storeFacts.Dhaka && `৳${storeFacts.Dhaka} inside Dhaka`, storeFacts.Outside && `৳${storeFacts.Outside} outside Dhaka`].filter(Boolean).join(' · ') });
        if (storeFacts.number) f.push({ icon: 'phone', title: 'Order by Phone', text: storeFacts.number });
        if (storeFacts.WhatsApp) f.push({ icon: 'chat', title: 'WhatsApp', text: storeFacts.WhatsApp });
        return f;
    };
    const setFact = (i, key, value) => setSeoTexts({ ...seoTexts, facts: seoTexts.facts.map((f, j) => (j === i ? { ...f, [key]: value } : f)) });

    // Search preview with the store's own fallbacks (SiteEjs seo.homeMeta): an
    // empty title is "<store> - <tagline>", an empty description the store
    // description. Google cuts long ones at roughly 60 / 160 characters.
    const previewName = localStorage.getItem('storeName') || 'My Store';
    const previewTitle = metaTitle || `${previewName} - ${storeTagline || 'Online Shopping'}`;
    const previewDescription = metaDescription || storeDescription || `Welcome to ${previewName}. Shop quality products online.`;
    const previewClip = (s, n) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

    const getWordCount = (html) => {
        const text = html.replace(/<[^>]*>/g, '').trim();
        if (!text) return 0;
        return text.split(/\s+/).length;
    };

    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className={styles.header}>
                <div>
                    <h1>Home Page SEO & AI</h1>
                    <p>Manage SEO meta tags, AI optimization, structured data, and home page article</p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button type="button" onClick={startAIGenerate} disabled={aiLoading}
                        style={{ padding: '8px 18px', background: aiLoading ? '#e8e8ee' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: aiLoading ? '#6d7175' : '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: aiLoading ? 'not-allowed' : 'pointer' }}>
                        {aiLoading ? 'Writing...' : 'AI Write'}
                    </button>
                    <button className={styles.addButton} onClick={handleSave} disabled={saving}>
                        <AiOutlineSave /> {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {showAiPrompt && (
                <div style={{ padding: '16px 20px', background: '#f0f0ff', borderRadius: '8px', marginBottom: '16px', border: '1px solid #d4d4ff' }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#202223', marginBottom: '10px' }}>Tell AI about your store:</p>
                    <input type="text" placeholder="What does your store sell? e.g. Fashion, beauty products, electronics"
                        value={aiStoreType} onChange={e => setAiStoreType(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }} />
                    <input type="text" placeholder="Describe your store briefly e.g. Affordable luxury fashion for women in Bangladesh"
                        value={aiAbout} onChange={e => setAiAbout(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '10px', boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
                        <select value={aiSection} onChange={e => setAiSection(e.target.value)}
                            style={{ padding: '6px 10px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '12px', background: '#fff' }}>
                            <option value="all">Generate All</option>
                            <option value="meta">Meta Tags Only</option>
                            <option value="ai">Store Description Only</option>
                            <option value="article">Article Only</option>
                            <option value="faqs">FAQs Only</option>
                        </select>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => setShowAiPrompt(false)} style={{ padding: '6px 14px', background: '#fff', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={handleAIGenerate} style={{ padding: '6px 14px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Generate</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab Navigation */}
            <div className={styles.tabContainer}>
                <div className={styles.tabButtons}>
                    <button
                        onClick={() => setActiveTab('meta')}
                        className={activeTab === 'meta' ? styles.tabActive : styles.tab}
                    >
                        Meta Tags
                    </button>
                    <button
                        onClick={() => setActiveTab('ai')}
                        className={activeTab === 'ai' ? styles.tabActive : styles.tab}
                    >
                        AI & Structured Data
                    </button>
                    <button
                        onClick={() => setActiveTab('article')}
                        className={activeTab === 'article' ? styles.tabActive : styles.tab}
                    >
                        Home Page Article
                    </button>
                    <button
                        onClick={() => setActiveTab('faqs')}
                        className={activeTab === 'faqs' ? styles.tabActive : styles.tab}
                    >
                        Homepage FAQs
                    </button>
                    <button
                        onClick={() => setActiveTab('texts')}
                        className={activeTab === 'texts' ? styles.tabActive : styles.tab}
                    >
                        Section Texts
                    </button>
                </div>
            </div>

            {/* Content Card */}
            <div className={styles.contentCard}>

                {/* Loading Skeleton for Content Area */}
                {loading ? (
                    <div>
                        {/* Section Title */}
                        <div className={styles.skeleton} style={{ width: '180px', height: '20px', marginBottom: '24px' }}></div>

                        {/* Form Field Skeletons */}
                        <div style={{ marginBottom: '20px' }}>
                            <div className={styles.skeleton} style={{ width: '90px', height: '14px', marginBottom: '8px' }}></div>
                            <div className={styles.skeleton} style={{ width: '100%', height: '42px', borderRadius: '6px' }}></div>
                            <div className={styles.skeleton} style={{ width: '140px', height: '12px', marginTop: '6px' }}></div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div className={styles.skeleton} style={{ width: '130px', height: '14px', marginBottom: '8px' }}></div>
                            <div className={styles.skeleton} style={{ width: '100%', height: '80px', borderRadius: '6px' }}></div>
                            <div className={styles.skeleton} style={{ width: '160px', height: '12px', marginTop: '6px' }}></div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div className={styles.skeleton} style={{ width: '110px', height: '14px', marginBottom: '8px' }}></div>
                            <div className={styles.skeleton} style={{ width: '100%', height: '42px', borderRadius: '6px' }}></div>
                            <div className={styles.skeleton} style={{ width: '180px', height: '12px', marginTop: '6px' }}></div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div className={styles.skeleton} style={{ width: '200px', height: '14px', marginBottom: '8px' }}></div>
                            <div className={styles.skeleton} style={{ width: '300px', height: '160px', borderRadius: '8px', marginBottom: '10px' }}></div>
                            <div className={styles.skeleton} style={{ width: '100%', height: '42px', borderRadius: '6px' }}></div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                            <div style={{ flex: 1 }}>
                                <div className={styles.skeleton} style={{ width: '140px', height: '14px', marginBottom: '8px' }}></div>
                                <div className={styles.skeleton} style={{ width: '100%', height: '42px', borderRadius: '6px' }}></div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <div className={styles.skeleton} style={{ width: '140px', height: '14px', marginBottom: '8px' }}></div>
                                <div className={styles.skeleton} style={{ width: '100%', height: '42px', borderRadius: '6px' }}></div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div className={styles.skeleton} style={{ width: '160px', height: '14px', marginBottom: '8px' }}></div>
                            <div className={styles.skeleton} style={{ width: '100%', height: '42px', borderRadius: '6px' }}></div>
                        </div>

                        {/* Google Preview Skeleton */}
                        <div style={{ background: '#f8f9fa', border: '1px solid #e1e3e5', borderRadius: '8px', padding: '16px', marginTop: '20px' }}>
                            <div className={styles.skeleton} style={{ width: '160px', height: '14px', marginBottom: '12px' }}></div>
                            <div className={styles.skeleton} style={{ width: '70%', height: '18px', marginBottom: '6px' }}></div>
                            <div className={styles.skeleton} style={{ width: '180px', height: '13px', marginBottom: '6px' }}></div>
                            <div className={styles.skeleton} style={{ width: '90%', height: '13px' }}></div>
                        </div>
                    </div>
                ) : (
                <>

                {/* ========== META TAGS TAB ========== */}
                {activeTab === 'meta' && (
                    <div>
                        <h3 className={styles.sectionTitle}>SEO Meta Tags</h3>

                        <div className={styles.formGroup}>
                            <label>Meta Title</label>
                            <input
                                type="text"
                                value={metaTitle}
                                onChange={(e) => setMetaTitle(e.target.value)}
                                placeholder="Enter meta title for your home page"
                                maxLength={70}
                            />
                            <small>{metaTitle.length}/70 characters {metaTitle.length > 60 ? '(recommended: under 60)' : ''}</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Meta Description</label>
                            <textarea
                                rows={3}
                                value={metaDescription}
                                onChange={(e) => setMetaDescription(e.target.value)}
                                placeholder="Enter meta description for your home page"
                                maxLength={200}
                            />
                            <small>{metaDescription.length}/200 characters {metaDescription.length > 160 ? '(recommended: under 160)' : ''}</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Meta Keywords</label>
                            <input
                                type="text"
                                value={metaKeywords}
                                onChange={(e) => setMetaKeywords(e.target.value)}
                                placeholder="keyword1, keyword2, keyword3 (comma-separated)"
                            />
                            <small>Separate keywords with commas. Google doesn't use the keywords tag; your title, description and page content are what count.</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>OG Image (Social Sharing Image)</label>
                            {ogImage && (
                                <div style={{ marginBottom: 10 }}>
                                    <img
                                        src={ogImage}
                                        alt="OG Preview"
                                        style={{ maxWidth: 300, maxHeight: 160, borderRadius: 8, border: '1px solid #e1e3e5', objectFit: 'cover' }}
                                    />
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleOgImageUpload}
                                disabled={uploadingImage}
                            />
                            {uploadingImage && <small>Uploading...</small>}
                            <input
                                type="text"
                                value={ogImage}
                                onChange={(e) => setOgImage(e.target.value)}
                                placeholder="Or paste image URL directly"
                                style={{ marginTop: 8 }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: 12 }}>
                            <div className={styles.formGroup} style={{ flex: 1 }}>
                                <label>OG Image Width (px)</label>
                                <input
                                    type="number"
                                    value={ogImageWidth}
                                    onChange={(e) => setOgImageWidth(e.target.value)}
                                    placeholder="800"
                                    min="1"
                                />
                                <small>Filled in from the image automatically (Facebook needs the exact size)</small>
                            </div>
                            <div className={styles.formGroup} style={{ flex: 1 }}>
                                <label>OG Image Height (px)</label>
                                <input
                                    type="number"
                                    value={ogImageHeight}
                                    onChange={(e) => setOgImageHeight(e.target.value)}
                                    placeholder="800"
                                    min="1"
                                />
                                <small>Recommended: 1200×630 (landscape) or 800×800 (square)</small>
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Facebook App ID (fb:app_id)</label>
                            <input
                                type="text"
                                value={facebookAppId}
                                onChange={(e) => setFacebookAppId(e.target.value)}
                                placeholder="e.g., 2657844754247562"
                            />
                            <small>Enables Facebook Analytics/Insights on shares. Get it from <strong>developers.facebook.com</strong> → Your App → Settings → Basic</small>
                            {facebookAppId.trim() && !/^\d{5,20}$/.test(facebookAppId.trim()) && (
                                <small className={styles.fieldWarn}>An App ID is a number (5–20 digits). It is added to your store once it is.</small>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label>OG Description (Social Sharing Description)</label>
                            <textarea
                                rows={2}
                                value={ogDescription}
                                onChange={(e) => setOgDescription(e.target.value)}
                                placeholder="Custom description shown when shared on social media"
                            />
                            <small>Leave empty to use Meta Description for social sharing</small>
                        </div>

                        {/* Google Preview */}
                        <div style={{
                            background: '#f8f9fa',
                            border: '1px solid #e1e3e5',
                            borderRadius: 8,
                            padding: 16,
                            marginTop: 20
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <AiOutlineEye style={{ fontSize: 18 }} />
                                <strong style={{ fontSize: 14, color: '#202223' }}>Google Search Preview</strong>
                            </div>
                            <div style={{ fontFamily: 'Arial, sans-serif' }}>
                                <div style={{ fontSize: 18, color: '#1a0dab', marginBottom: 4, lineHeight: 1.3 }}>
                                    {previewClip(previewTitle, 60)}
                                </div>
                                <div style={{ fontSize: 13, color: '#006621', marginBottom: 4 }}>
                                    {storeUrl || 'https://yourstore.com'}
                                </div>
                                <div style={{ fontSize: 13, color: '#545454', lineHeight: 1.4 }}>
                                    {previewClip(previewDescription, 160)}
                                </div>
                            </div>
                            <small style={{ display: 'block', marginTop: 10, fontSize: 12, color: '#6d7175' }}>
                                Empty fields fall back as on your store: title = store name + tagline, description = store description. Google may still shorten or rewrite them.
                            </small>
                        </div>
                    </div>
                )}

                {/* ========== AI & STRUCTURED DATA TAB ========== */}
                {activeTab === 'ai' && (
                    <div>
                        <h3 className={styles.sectionTitle}>AI & Structured Data Settings</h3>

                        <div className={styles.formGroup}>
                            <label>Store Description (AI Optimization)</label>
                            <textarea
                                rows={5}
                                value={storeDescription}
                                onChange={(e) => setStoreDescription(e.target.value)}
                                placeholder="Write a detailed description of your store for AI crawlers and search engines. Include what you sell, your target audience, and what makes your store unique."
                            />
                            <small>Helps search engines and AI assistants understand your store; also used as the description when Meta Description is empty</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Store Tagline / Slogan</label>
                            <input
                                type="text"
                                value={storeTagline}
                                onChange={(e) => setStoreTagline(e.target.value)}
                                placeholder="e.g., Quality Products, Fast Delivery"
                            />
                            <small>A short phrase about your store; also used in the page title when Meta Title is empty</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>SEO Language</label>
                            <select
                                value={seoLanguage}
                                onChange={(e) => setSeoLanguage(e.target.value)}
                            >
                                <option value="bn">Bengali (বাংলা)</option>
                                <option value="en">English</option>
                            </select>
                            <small>Primary language of your store content (tells Google the page language)</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Robots Meta Tag</label>
                            <input
                                type="text"
                                value={robotsMeta}
                                onChange={(e) => setRobotsMeta(e.target.value)}
                                placeholder="index, follow, max-image-preview:large"
                            />
                            <small>Controls how search engines index and display your pages</small>
                            {/\b(noindex|none)\b/i.test(robotsMeta) && (
                                <small className={styles.fieldWarn}>
                                    "noindex" / "none" removes your store from Google and other search engines. Recommended: index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1
                                </small>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label>SEO Author</label>
                            <input
                                type="text"
                                value={seoAuthor}
                                onChange={(e) => setSeoAuthor(e.target.value)}
                                placeholder="Store owner name or brand name"
                            />
                            <small>Author meta tag for your store pages</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Structured Data Type (Schema.org)</label>
                            <select
                                value={structuredDataType}
                                onChange={(e) => setStructuredDataType(e.target.value)}
                            >
                                <option value="OnlineStore">Online Store (recommended)</option>
                                <option value="Organization">Organization</option>
                                <option value="LocalBusiness">Local Business (needs your shop address)</option>
                                <option value="Store">Physical Store (needs your shop address)</option>
                            </select>
                            <small>Google asks for the most specific type: Online Store for a web shop. Local Business and Physical Store need the shop address in your store settings; without it your store uses Online Store.</small>
                        </div>

                        {/* Info about AI */}
                        <div className={styles.infoNote}>
                            <p>
                                <strong>Why AI Optimization matters:</strong> AI search and assistants (Google AI Overviews, ChatGPT, Gemini, Claude, Perplexity, Copilot) use your store description, tagline, FAQs and structured data to understand and recommend your store. A detailed, accurate description helps AI systems present your products to potential customers.
                            </p>
                        </div>
                    </div>
                )}

                {/* ========== HOME PAGE ARTICLE TAB ========== */}
                {activeTab === 'article' && (
                    <div>
                        <h3 className={styles.sectionTitle}>Home Page Article</h3>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={articleEnabled}
                                    onChange={(e) => setArticleEnabled(e.target.checked)}
                                />
                                Enable Article on Home Page
                            </label>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Article Title</label>
                            <input
                                type="text"
                                value={articleTitle}
                                onChange={(e) => setArticleTitle(e.target.value)}
                                placeholder="e.g., About Our Store, Why Shop With Us"
                                disabled={!articleEnabled}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Article Position on Home Page</label>
                            <select
                                value={articlePosition}
                                onChange={(e) => setArticlePosition(e.target.value)}
                                disabled={!articleEnabled}
                            >
                                <option value="below_hero">Below Banner (Top)</option>
                                <option value="middle">Middle (After Trending Products)</option>
                                <option value="above_footer">Above Footer (Bottom)</option>
                            </select>
                            <small>Choose where the article section appears on the home page</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Article Content</label>
                            <div style={{
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                minHeight: 250,
                                opacity: articleEnabled ? 1 : 0.5,
                                pointerEvents: articleEnabled ? 'auto' : 'none'
                            }}>
                                <Editor
                                    editorState={editorState}
                                    onEditorStateChange={setEditorState}
                                    toolbar={{
                                        options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'image', 'history'],
                                        inline: {
                                            options: ['bold', 'italic', 'underline'],
                                        },
                                        image: {
                                            uploadCallback: uploadCallback,
                                            previewImage: true,
                                            alt: { present: true, mandatory: false },
                                        },
                                    }}
                                    editorStyle={{
                                        padding: '0 12px',
                                        minHeight: 200,
                                        fontSize: 14,
                                    }}
                                />
                            </div>
                            <small>
                                Word count: {getWordCount(stateToHTML(editorState.getCurrentContent()))}
                                {' '}(Recommended: 300-500 words for SEO)
                            </small>
                        </div>

                        <div className={styles.infoNote}>
                            <p>
                                <strong>SEO Tip:</strong> Google ranks helpful content written for people, not for search engines. Write about what your customers want to know: what you sell, delivery areas and charges, payment, returns, and why to buy from you. 300-500 words is a good length; link to your categories and products where it helps, and don't repeat keywords unnaturally. This content also helps AI assistants recommend your store.
                            </p>
                        </div>
                    </div>
                )}

                {/* ========== HOMEPAGE FAQs TAB ========== */}
                {activeTab === 'faqs' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 className={styles.sectionTitle} style={{ margin: 0 }}>Homepage FAQs</h3>
                            <button
                                type="button"
                                className={styles.addButton}
                                onClick={() => setHomePageFaqs([...homePageFaqs, { question: '', answer: '' }])}
                                style={{ padding: '8px 16px', fontSize: 13 }}
                            >
                                <AiOutlinePlus /> Add FAQ
                            </button>
                        </div>

                        {homePageFaqs.length === 0 ? (
                            <div className={styles.emptyState}>
                                <p>No FAQs added yet. Click "Add FAQ" to create your first FAQ.</p>
                                <p style={{ fontSize: 13, marginTop: 8, color: '#9ca3af' }}>
                                    FAQs answer shoppers' common questions, and AI assistants and search engines read them.
                                </p>
                            </div>
                        ) : (
                            <div className={styles.hideScrollbarFAQ} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                {homePageFaqs.map((faq, index) => (
                                    <div key={index} style={{
                                        border: '1px solid #e1e3e5',
                                        borderRadius: 8,
                                        padding: 16,
                                        marginBottom: 12,
                                        background: '#fafafa'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#6d7175' }}>FAQ #{index + 1}</span>
                                            <button
                                                type="button"
                                                className={styles.deleteBtn}
                                                onClick={() => {
                                                    const updated = homePageFaqs.filter((_, i) => i !== index);
                                                    setHomePageFaqs(updated);
                                                }}
                                                title="Remove this FAQ"
                                            >
                                                <AiOutlineDelete />
                                            </button>
                                        </div>
                                        <div className={styles.formGroup} style={{ marginBottom: 12 }}>
                                            <label style={{ fontSize: 13 }}>Question</label>
                                            <input
                                                type="text"
                                                value={faq.question}
                                                onChange={(e) => {
                                                    const updated = [...homePageFaqs];
                                                    updated[index] = { ...updated[index], question: e.target.value };
                                                    setHomePageFaqs(updated);
                                                }}
                                                placeholder="Enter FAQ question"
                                            />
                                        </div>
                                        <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                                            <label style={{ fontSize: 13 }}>Answer</label>
                                            <textarea
                                                rows={3}
                                                value={faq.answer}
                                                onChange={(e) => {
                                                    const updated = [...homePageFaqs];
                                                    updated[index] = { ...updated[index], answer: e.target.value };
                                                    setHomePageFaqs(updated);
                                                }}
                                                placeholder="Enter FAQ answer"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={{ marginTop: 16, fontSize: 13, color: '#6d7175' }}>
                            Total FAQs: {homePageFaqs.filter(f => f.question.trim() && f.answer.trim()).length}
                            {homePageFaqs.some(f => !f.question.trim() || !f.answer.trim()) &&
                                <span style={{ color: '#f59e0b', marginLeft: 8 }}>
                                    (Empty FAQs will be removed on save)
                                </span>
                            }
                        </div>

                        <div className={styles.infoNote} style={{ marginTop: 16 }}>
                            <p>
                                <strong>SEO Tip:</strong> FAQs answer the questions shoppers ask before they buy, and AI assistants (ChatGPT, Gemini, Claude, Perplexity) and other search engines read them when answering questions about your store. Google stopped showing FAQ rich results in May 2026, so they won't appear as extra lines under your Google result. Write real questions customers ask, with clear 2-3 sentence answers, and no ads in the answers. Aim for 8-15 FAQs on your products, delivery, returns and payment.
                            </p>
                        </div>
                    </div>
                )}

                {/* ========== SECTION TEXTS TAB ========== */}
                {activeTab === 'texts' && (
                    <div>
                        <h3 className={styles.sectionTitle}>Section Texts</h3>
                        <p style={{ fontSize: 13, color: '#6d7175', margin: '0 0 18px', lineHeight: 1.5 }}>
                            The words in your store's SEO sections on the home, category and product pages (FAQ and reviews) and in its header and footer. Write them in your store's language; leave a field empty to use the default shown in grey.
                        </p>

                        {TEXT_FIELDS.map(([key, label, def, max]) => (
                            <div className={styles.formGroup} key={key}>
                                <label>{label}</label>
                                <input
                                    type="text"
                                    value={seoTexts[key]}
                                    onChange={(e) => setSeoTexts({ ...seoTexts, [key]: e.target.value })}
                                    placeholder={def}
                                    maxLength={max}
                                />
                            </div>
                        ))}

                        <h3 className={styles.sectionTitle} style={{ marginTop: 28 }}>Header and footer</h3>
                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={seoTexts.showTopbar}
                                    onChange={(e) => setSeoTexts({ ...seoTexts, showTopbar: e.target.checked })}
                                />
                                Show the top bar above the header (your text, phone and WhatsApp)
                            </label>
                        </div>
                        {seoTexts.showTopbar && (
                            <div className={styles.formGroup}>
                                <label>Top bar text</label>
                                <input
                                    type="text"
                                    value={seoTexts.topbarText}
                                    onChange={(e) => setSeoTexts({ ...seoTexts, topbarText: e.target.value })}
                                    placeholder={storeFacts.Dhaka || storeFacts.Outside
                                        ? `Delivery ${[storeFacts.Dhaka && `৳${storeFacts.Dhaka} inside Dhaka`, storeFacts.Outside && `৳${storeFacts.Outside} outside Dhaka`].filter(Boolean).join(' · ')}`
                                        : 'e.g. Cash on delivery all over Bangladesh'}
                                    maxLength={120}
                                />
                                <small style={{ display: 'block', marginTop: 6, fontSize: 12, color: '#6d7175' }}>
                                    Empty = your delivery charges. On phones only the text shows; the phone and WhatsApp buttons are in the menu.
                                </small>
                            </div>
                        )}
                        {CHROME_FIELDS.map(([key, label, def, max]) => (
                            <div className={styles.formGroup} key={key}>
                                <label>{label}</label>
                                <input
                                    type="text"
                                    value={seoTexts[key]}
                                    onChange={(e) => setSeoTexts({ ...seoTexts, [key]: e.target.value })}
                                    placeholder={def}
                                    maxLength={max}
                                />
                            </div>
                        ))}
                        <div className={styles.formGroup}>
                            <label>Footer: about your store</label>
                            <textarea
                                value={seoTexts.footerAbout}
                                onChange={(e) => setSeoTexts({ ...seoTexts, footerAbout: e.target.value })}
                                placeholder="Empty = the first sentence of your Store Description (AI & Structured Data tab)"
                                maxLength={300}
                                rows={3}
                            />
                        </div>
                        <small style={{ display: 'block', margin: '-6px 0 8px', fontSize: 12, color: '#6d7175' }}>
                            The header and footer take their colours from your theme colours, and their phone, WhatsApp, email, address and social links from your store settings. The footer links to the policy pages you have written.
                        </small>

                        <h3 className={styles.sectionTitle} style={{ marginTop: 28 }}>Facts under the home page heading</h3>
                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={seoTexts.showFacts}
                                    onChange={(e) => setSeoTexts({ ...seoTexts, showFacts: e.target.checked })}
                                />
                                Show facts (with the home page article or FAQs)
                            </label>
                        </div>

                        {seoTexts.showFacts && seoTexts.facts.length === 0 && (
                            <div style={{ border: '1px solid #e1e3e5', borderRadius: 8, padding: 16, background: '#fafafa' }}>
                                <div style={{ fontSize: 13, color: '#6d7175', marginBottom: 10 }}>Automatic, from your store settings:</div>
                                {autoFacts().map((f) => (
                                    <div key={f.icon} style={{ fontSize: 14, color: '#202223', marginBottom: 6 }}>
                                        <strong>{f.title}</strong> — {f.text}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className={styles.addButton}
                                    onClick={() => setSeoTexts({ ...seoTexts, facts: autoFacts() })}
                                    style={{ marginTop: 10, padding: '8px 16px', fontSize: 13 }}
                                >
                                    Customize facts
                                </button>
                            </div>
                        )}

                        {seoTexts.showFacts && seoTexts.facts.length > 0 && (
                            <div>
                                {seoTexts.facts.map((f, i) => (
                                    <div key={i} className={styles.factRow}>
                                        <select value={f.icon} onChange={(e) => setFact(i, 'icon', e.target.value)} aria-label={`Fact ${i + 1} icon`} style={{ padding: '9px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff' }}>
                                            {FACT_ICONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                        <input type="text" value={f.title} onChange={(e) => setFact(i, 'title', e.target.value)} placeholder="Title" maxLength={60} aria-label={`Fact ${i + 1} title`} style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
                                        <input type="text" value={f.text} onChange={(e) => setFact(i, 'text', e.target.value)} placeholder="Text" maxLength={120} aria-label={`Fact ${i + 1} text`} style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
                                        <button type="button" className={styles.deleteBtn} onClick={() => setSeoTexts({ ...seoTexts, facts: seoTexts.facts.filter((_, j) => j !== i) })} title="Remove this fact">
                                            <AiOutlineDelete />
                                        </button>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                                    {seoTexts.facts.length < 6 && (
                                        <button type="button" className={styles.addButton} onClick={() => setSeoTexts({ ...seoTexts, facts: [...seoTexts.facts, { icon: 'star', title: '', text: '' }] })} style={{ padding: '8px 16px', fontSize: 13 }}>
                                            <AiOutlinePlus /> Add fact
                                        </button>
                                    )}
                                    <button type="button" onClick={() => setSeoTexts({ ...seoTexts, facts: [] })} style={{ padding: '8px 16px', fontSize: 13, background: '#fff', border: '1px solid #c9cccf', borderRadius: 8, cursor: 'pointer' }}>
                                        Use automatic facts
                                    </button>
                                </div>
                                <small style={{ display: 'block', marginTop: 10, fontSize: 12, color: '#6d7175' }}>
                                    Up to 6 facts. A Phone or WhatsApp fact links to the mobile number written in its text.
                                </small>
                            </div>
                        )}
                    </div>
                )}
                </>)}
            </div>
        </div>
    );
}

export default HomePageSEO;
